export const RESPONSIVE_WIDTHS = [256, 384, 512, 768, 1024, 1280, 1920] as const;

export type ResponsiveWidth = (typeof RESPONSIVE_WIDTHS)[number];
export type ResponsiveFormat = 'jpg' | 'webp' | 'avif';

const CHAT_PANEL_PADDING = 24; // 12px padding on each side
const GRID_GAP = 8;

/** sizes attr for a single image spanning full chat width */
export const SIZES_FULL = `calc(100vw - ${CHAT_PANEL_PADDING}px)`;

/** sizes attr for an image in a 2-column grid */
export const SIZES_HALF = `calc(50vw - ${(CHAT_PANEL_PADDING + GRID_GAP) / 2}px)`;

const IMAGE_URL_RE = /^(.+)\.([a-zA-Z]+)(\?.*)?$/;
const ALREADY_RESPONSIVE_RE = /_w\d+$/;

/**
 * Extracts the base path (without extension) from an image URL.
 * Returns null if the URL doesn't match the expected pattern
 * or is already a responsive variant.
 */
const upgradeToHttps = (url: string): string =>
  url.startsWith('http://') ? url.replace('http://', 'https://') : url;

const parseImageBase = (src: string): string | null => {
  const match = upgradeToHttps(src).match(IMAGE_URL_RE);
  if (!match) return null;

  const basePath = match[1];
  if (ALREADY_RESPONSIVE_RE.test(basePath)) return null;

  return basePath;
};

/**
 * Builds a srcSet string for a given format, excluding widths
 * that exceed the original image width (backend won't have those).
 */
export const buildSrcSet = (
  src: string,
  format: ResponsiveFormat,
  originalWidth: number,
): string | null => {
  const base = parseImageBase(src);
  if (!base) return null;

  const widths = RESPONSIVE_WIDTHS.filter((w) => w <= originalWidth);
  if (widths.length === 0) return null;

  return widths.map((w) => `${base}_w${w}.${format} ${w}w`).join(', ');
};

/**
 * Builds a single responsive URL for the largest available width.
 * Used for lightbox where we need one specific URL.
 */
export const buildLargestResponsiveUrl = (
  src: string,
  originalWidth: number,
  format: ResponsiveFormat = 'webp',
): string | null => {
  const base = parseImageBase(src);
  if (!base) return null;

  const widths = RESPONSIVE_WIDTHS.filter((w) => w <= originalWidth);
  if (widths.length === 0) return null;

  const largest = widths[widths.length - 1];
  return `${base}_w${largest}.${format}`;
};
